import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Typography,
} from '@mui/material';
import clientService from 'src/clientService';
import { SoilElementResponse, SoilElementCreate } from 'src/api/Api';
import { useSnackbar, SnackbarType } from 'src/view/components/SnackbarService';

interface SoilElementCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (newElement: SoilElementResponse) => void;
}

const CATEGORY_OPTIONS = [
  'Nutrients',
  'Macro-Nutrient',
  'Micro-Nutrient',
  'Carbon',
  'Chemical',
  'Physical',
  'Heavy Metal',
  'Other',
];

const UNIT_OPTIONS = ['%', 'mg/kg', 'ppm', 'pH', 'g/cm³', 'cmol/kg', 'dS/m', 'meq/100g'];

export const SoilElementCreateDialog: React.FC<SoilElementCreateDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<SoilElementCreate>({
    name: '',
    symbol: '',
    category: 'Nutrients',
    default_unit: '%',
    description: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const nameTrimmed = (formData.name || '').trim();
    const symbolTrimmed = (formData.symbol || '').trim();
    if (!nameTrimmed || !symbolTrimmed) {
      showSnackbar({
        message: 'Element name and chemical symbol are required.',
        type: SnackbarType.ERROR,
      });
      return;
    }

    setLoading(true);
    try {
      const response = await clientService.api.createSoilElementApiV1SoilChemistryElementsPost({
        name: nameTrimmed,
        symbol: symbolTrimmed,
        category: formData.category,
        default_unit: formData.default_unit,
        description: formData.description?.trim() || undefined,
        is_active: true,
      });
      const created = response.data;
      showSnackbar({
        message: `Successfully created chemical element "${created.name}"`,
        type: SnackbarType.SUCCESS,
      });
      onSuccess(created);
      onClose();
      // Reset form
      setFormData({
        name: '',
        symbol: '',
        category: 'Nutrients',
        default_unit: '%',
        description: '',
      });
    } catch (err: any) {
      console.error('Failed to create soil element:', err);
      showSnackbar({
        message: err?.response?.data?.detail || 'Failed to create soil element',
        type: SnackbarType.ERROR,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          <Typography variant="h6" fontWeight="bold">
            Create Custom Soil Element
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Add a new parameter or chemical element to the catalog.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}>
              <TextField
                name="name"
                label="Element Name"
                value={formData.name}
                onChange={handleChange}
                required
                fullWidth
                placeholder="e.g. Total Nitrogen"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                name="symbol"
                label="Symbol / Code"
                value={formData.symbol}
                onChange={handleChange}
                required
                fullWidth
                placeholder="e.g. N"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="category"
                label="Category"
                select
                value={formData.category}
                onChange={handleChange}
                fullWidth
              >
                {CATEGORY_OPTIONS.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="default_unit"
                label="Default Unit"
                select
                value={formData.default_unit}
                onChange={handleChange}
                fullWidth
              >
                {UNIT_OPTIONS.map((u) => (
                  <MenuItem key={u} value={u}>
                    {u}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="description"
                label="Description"
                value={formData.description ?? ''}
                onChange={handleChange}
                multiline
                rows={3}
                fullWidth
                placeholder="Optional notes or standard threshold instructions for this parameter."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? 'Creating...' : 'Save Element'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
